import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface PullRequest {
  id: string;
  prNumber: number;
  prUrl: string;
  branchName: string;
  title: string;
  status: string; // Status code (open, merged, closed)
  filesModified: number;
  improvementSummary: string | null;
  mergedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Flattened repository info
  repositoryId: string;
  repositoryOwner: string;
  repositoryName: string;
}

interface PullRequestsState {
  pullRequests: PullRequest[];
  currentPullRequest: PullRequest | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: PullRequestsState = {
  pullRequests: [],
  currentPullRequest: null,
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getPullRequests = createAsyncThunk(
  'pullRequests/getPullRequests',
  async (params: { repositoryId?: number; status?: string; userId?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.repositoryId) queryParams.append('repositoryId', params.repositoryId.toString());
      if (params.status) queryParams.append('status', params.status);
      if (params.userId) queryParams.append('userId', params.userId);

      const endpoint = `/pull-requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiRequest(endpoint);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get pull requests
  [getPullRequests.pending.type]: { status: 'loading' as const },
  [getPullRequests.fulfilled.type]: (state: PullRequestsState, action: any) => {
    if (!action.payload) {
      state.pullRequests = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    const pullRequests = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.pullRequests || action.payload.pullRequests || [];

    state.pullRequests = pullRequests;
    state.status = 'success';
    state.error = '';
  },
  [getPullRequests.rejected.type]: (state: PullRequestsState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch pull requests';
  },
};

// ==============================|| SLICE ||============================== //

export const pullRequestsSlice = createSlice({
  name: 'pullRequests',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentPullRequest(state, action) {
      state.currentPullRequest = action.payload;
    },
    clearPullRequestsState(state) {
      state.pullRequests = [];
      state.currentPullRequest = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentPullRequest, clearPullRequestsState } = pullRequestsSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectPullRequests = (state: any) => state.pullRequests.pullRequests;
export const selectCurrentPullRequest = (state: any) => state.pullRequests.currentPullRequest;
export const selectPullRequestsStatus = (state: any) => state.pullRequests.status;
export const selectPullRequestsError = (state: any) => state.pullRequests.error;

export default pullRequestsSlice.reducer;
